import { IS_PROD_ZONE } from '@/constants/env';
import { AdPageType, EAdType } from '@/models/ad/list';
import { Client, ClientType } from '@/types/app';

export interface AdControlRequest {
  unitId: string;
  userId?: string;
  platform?: string;
  locale?: string;
  contextId?: string;
  trackMode?: number;
}

export interface AdControlPropertyRequest {
  uid: string;
  value?: { BIRTH?: string; GDR?: 'M' | 'F' | 'O'; AGE?: number };
}

export interface AdControlResponse {
  code: number;
  msg: string;
  result: {
    format: string;
    unit: string;
    w: number;
    h: number;
    ad: string;
  };
}

export interface AdControlNativeResponse {
  code: number;
  msg: string;
  result: {
    format: string;
    unit: string;
    advertiser: {
      name: string;
      domain: string;
    };
    extra: {
      button?: string;
      tag?: string;
      extra_tag?: string;
    };
    headline: string;
    body: string;
    destinationURL: string;
    asset?: string;
    pixelTracker?: string;
    imprTracker?: string;
    clickTracker?: string;
  };
}

export type AdType = 'banner' | 'native';

// 제네릭 타입 맵핑
export type AdControlResponseMap = {
  banner: AdControlResponse;
  native: AdControlNativeResponse;
};

export interface AdControlBannerRequest extends AdControlRequest {
  adType: 'banner';
}

export interface AdControlNativeRequest extends AdControlRequest {
  adType: 'native';
}

export type AdControlTypedRequest<T extends AdType> = AdControlRequest & {
  adType: T;
};

const AD_PAGE_TYPE_TO_AD_TYPE: Record<AdPageType, Record<ClientType, EAdType | null>> = {
  [AdPageType.MagazineDetail]: {
    [ClientType.WEB]: EAdType.USER_WEB_MAGAZINE_ARTICLE_CONTENT_BANNER,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_MAGAZINE_ARTICLE_CONTENT_BANNER,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_MAGAZINE_ARTICLE_CONTENT_BANNER,
  },
  [AdPageType.Search]: {
    [ClientType.WEB]: EAdType.USER_WEB_SEARCH_MAIN,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_SEARCH_MAIN,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_SEARCH_MAIN_IMAGE,
  },
  [AdPageType.NonBenefitDetail]: {
    [ClientType.WEB]: EAdType.USER_WEB_NON_BENEFIT_PRICE_MIDDLE,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_NON_BENEFIT_PRICE_MIDDLE,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerBottomBanner]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_QUESTION_BOTTOM,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_QUESTION_BOTTOM,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_QNA_QUESTION_BOTTOM_IMAGE,
  },
  [AdPageType.Menu]: {
    [ClientType.WEB]: EAdType.USER_WEB_MENU_BOTTOM,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_MENU_BOTTOM,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.TopCommonBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_TOP_STICKY_BAND_BANNER,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaHomeBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_QNA_HOME_TOP_BANNER,
  },
  [AdPageType.QnaHomeNative]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_QNA_HOME_MIDDLE_NATIVE,
  },
  [AdPageType.QnaListBanner]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_LIST_TOP_BANNER,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_LIST_TOP_BANNER,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaListNative]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_LIST_MIDDLE_NATIVE,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_LIST_MIDDLE_NATIVE,
    [ClientType.MOBILE_WEBVIEW]: EAdType.USER_APP_QNA_LIST_MIDDLE_NATIVE,
  },
  [AdPageType.QnaDetailBottomBanner]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_LOWEST_BOTTOM_BANNER,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_LOWEST_BOTTOM_BANNER,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerMiddleBanner]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_ANSWER_MIDDLE_BANNER,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_ANSWER_MIDDLE_BANNER,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailQuestionBottomBanner]: {
    [ClientType.WEB]: EAdType.USER_WEB_QNA_QUESTION_BOTTOM_BANNER,
    [ClientType.MOBILE_WEB]: EAdType.USER_WEB_MOBILE_QNA_QUESTION_BOTTOM_BANNER,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
};

const AdropAspectMap: Record<AdPageType, Record<ClientType, number>> = {
  [AdPageType.MagazineDetail]: {
    [ClientType.WEB]: 722 / 174,
    [ClientType.MOBILE_WEB]: 312 / 174,
    [ClientType.MOBILE_WEBVIEW]: 312 / 174,
  },
  [AdPageType.TopCommonBanner]: {
    [ClientType.WEB]: 0,
    [ClientType.MOBILE_WEB]: 360 / 72,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.Search]: {
    [ClientType.WEB]: 720 / 80,
    [ClientType.MOBILE_WEB]: 312 / 80,
    [ClientType.MOBILE_WEBVIEW]: 312 / 80,
  },
  [AdPageType.NonBenefitDetail]: {
    [ClientType.WEB]: 720 / 120,
    [ClientType.MOBILE_WEB]: 312 / 96,
    [ClientType.MOBILE_WEBVIEW]: 312 / 96,
  },
  [AdPageType.QnaDetailAnswerBottomBanner]: {
    [ClientType.WEB]: 720 / 90,
    [ClientType.MOBILE_WEB]: 312 / 100,
    [ClientType.MOBILE_WEBVIEW]: 312 / 100,
  },
  [AdPageType.Menu]: {
    [ClientType.WEB]: 248 / 96,
    [ClientType.MOBILE_WEB]: 312 / 96,
    [ClientType.MOBILE_WEBVIEW]: 312 / 96,
  },
  [AdPageType.QnaHomeBanner]: {
    [ClientType.WEB]: 0,
    [ClientType.MOBILE_WEB]: 312 / 80,
    [ClientType.MOBILE_WEBVIEW]: 312 / 80,
  },
  [AdPageType.QnaListBanner]: {
    [ClientType.WEB]: 720 / 80,
    [ClientType.MOBILE_WEB]: 312 / 80,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.QnaListNative]: {
    [ClientType.WEB]: 0,
    [ClientType.MOBILE_WEB]: 0,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.QnaHomeNative]: {
    [ClientType.WEB]: 0,
    [ClientType.MOBILE_WEB]: 0,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.QnaDetailBottomBanner]: {
    [ClientType.WEB]: 768 / 280,
    [ClientType.MOBILE_WEB]: 312 / 280,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.QnaDetailAnswerMiddleBanner]: {
    [ClientType.WEB]: 768 / 280,
    [ClientType.MOBILE_WEB]: 312 / 280,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
  [AdPageType.QnaDetailQuestionBottomBanner]: {
    [ClientType.WEB]: 720 / 90,
    [ClientType.MOBILE_WEB]: 312 / 100,
    [ClientType.MOBILE_WEBVIEW]: 0,
  },
};

const AdropKeyMap: Record<AdPageType, Record<ClientType, string | null>> = {
  [AdPageType.MagazineDetail]: {
    [ClientType.WEB]: '01K0R60GFMTPW20ME1QM5H5SE8',
    [ClientType.MOBILE_WEB]: '01K0R5X1PH35WGFN96W49YCYKB',
    [ClientType.MOBILE_WEBVIEW]: '01K44YS4CFTGE86PVMKFDK9WJ7',
  },
  [AdPageType.Search]: {
    [ClientType.WEB]: '01K0R5V65TPN8XFG5Y8R9YVVVW',
    [ClientType.MOBILE_WEB]: '01K0R564ASW381N5CVVEEDBA0V',
    [ClientType.MOBILE_WEBVIEW]: '01K44YC9YHRFKXFWG9FX0B4T83',
  },
  [AdPageType.NonBenefitDetail]: {
    [ClientType.WEB]: '01K0R5DK2GVZWD6WYBMS90SZV0',
    [ClientType.MOBILE_WEB]: '01K0R54160KAT3VKZNCKKWVDMT',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerBottomBanner]: {
    [ClientType.WEB]: '01K0R5Z6AJ1CNY132Q8T70X8N7',
    [ClientType.MOBILE_WEB]: '01K0R5W275ZWN0FDNY71R9Z73Z',
    [ClientType.MOBILE_WEBVIEW]: '01K3QZPYW91G300N0NVS1P5ABY',
  },
  [AdPageType.Menu]: {
    [ClientType.WEB]: '01K0R61G6DM9SDFA5NPF5YMVBH',
    [ClientType.MOBILE_WEB]: '01K0R5Y54YWT1MAFAKM114WVQY',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.TopCommonBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: '01K9C722KTTJ25874DGWVV73TE',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaHomeBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: '01K44ZJ2MM7PM6GDFM10F0B8HX',
  },
  [AdPageType.QnaListBanner]: {
    [ClientType.WEB]: '01K44ZZWW2TNSGCATZ8CGWHVGF',
    [ClientType.MOBILE_WEB]: '01K44ZZBDV4Y9688SD2HVQ84V6',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaListNative]: {
    [ClientType.WEB]: '01K450A1MRAMW2CHF83025QRG5',
    [ClientType.MOBILE_WEB]: '01K45078E7HKAC9E3QWY7ACGQR',
    [ClientType.MOBILE_WEBVIEW]: '01K44Z9J0S6QXWNK7RD4777681',
  },
  [AdPageType.QnaHomeNative]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: '01K44ZFEJ370KK79K7MW68XY5D',
  },
  [AdPageType.QnaDetailBottomBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerMiddleBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailQuestionBottomBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
};

const GoogleAdSenseKeyMap: Record<AdPageType, Record<ClientType, string | null>> = {
  [AdPageType.MagazineDetail]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.Search]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.NonBenefitDetail]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.Menu]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.TopCommonBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: '1712610913',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaHomeBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaListBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaListNative]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaHomeNative]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerBottomBanner]: {
    [ClientType.WEB]: '4632498318',
    [ClientType.MOBILE_WEB]: '7242169570',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailBottomBanner]: {
    [ClientType.WEB]: '5008946411',
    [ClientType.MOBILE_WEB]: '5008946411',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerMiddleBanner]: {
    [ClientType.WEB]: '9102489693',
    [ClientType.MOBILE_WEB]: '9102489693',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailQuestionBottomBanner]: {
    [ClientType.WEB]: '5363266996',
    [ClientType.MOBILE_WEB]: '5363266996',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
};

const TestAdropKeyMap: Record<AdPageType, Record<ClientType, string | null>> = {
  [AdPageType.MagazineDetail]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_320_50',
  },
  [AdPageType.Search]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_320_50',
  },
  [AdPageType.NonBenefitDetail]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerBottomBanner]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_320_50',
  },
  [AdPageType.Menu]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.TopCommonBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaHomeBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_320_50',
  },
  [AdPageType.QnaListBanner]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_320_50',
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaListNative]: {
    [ClientType.WEB]: 'PUBLIC_TEST_UNIT_ID_NATIVE',
    [ClientType.MOBILE_WEB]: 'PUBLIC_TEST_UNIT_ID_NATIVE',
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_NATIVE',
  },
  [AdPageType.QnaHomeNative]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: 'PUBLIC_TEST_UNIT_ID_NATIVE',
  },
  [AdPageType.QnaDetailBottomBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailAnswerMiddleBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
  [AdPageType.QnaDetailQuestionBottomBanner]: {
    [ClientType.WEB]: null,
    [ClientType.MOBILE_WEB]: null,
    [ClientType.MOBILE_WEBVIEW]: null,
  },
};

export class AdropParamsFactory {
  private readonly adPageType: AdPageType;
  private readonly client: Client;

  constructor(adPageType: AdPageType, client: Client) {
    this.adPageType = adPageType;
    this.client = client;
  }

  getAdPageType(): AdPageType {
    return this.adPageType;
  }

  getUnitId(): string | null {
    return (
      (IS_PROD_ZONE
        ? AdropKeyMap?.[this.adPageType]?.[this.client.clientType]
        : TestAdropKeyMap?.[this.adPageType]?.[this.client.clientType]) || null
    );
  }

  getInnerUnitId(): EAdType | null {
    return AD_PAGE_TYPE_TO_AD_TYPE[this.adPageType][this.client.clientType] || null;
  }

  getGoogleAdSenseSlot(): string | null {
    return GoogleAdSenseKeyMap[this.adPageType]?.[this.client.clientType] || null;
  }

  getPlatform() {
    if (!this.client.isMobile) {
      return 'web';
    }
    if (this.client.isIOS) {
      return 'ios';
    }
    return 'android';
  }

  getAspect() {
    return AdropAspectMap[this.adPageType][this.client.clientType];
  }
}
